import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectSeparator, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { CostTicketFilter, CostTicketTypes } from '@/types/analyticsV2';
import { humanizeKey, withCount } from '../cost-format';

const ALL = 'all';
const UNCLASSIFIED = '__unclassified';

/** One Select value per ticket type: [category] for a whole category, [category, intent] for one intent. */
const typeValue = (category?: string | null, intent?: string | null): string =>
  category ? JSON.stringify(intent ? [category, intent] : [category]) : ALL;

const hasCostFilter = (f: CostTicketFilter) => Boolean(f.channel || f.category || f.intent);

/** Channel and ticket-type (intent by category) pickers for the AI cost tab. */
export function CostFilters({
  types,
  filter,
  onChange,
}: {
  types: CostTicketTypes | undefined;
  filter: CostTicketFilter;
  onChange: (filter: CostTicketFilter) => void;
}) {
  const disabled = !types;

  return (
    <div className='flex flex-wrap items-center gap-2' role='group' aria-label='Ticket filters'>
      <Select
        value={filter.channel ?? ALL}
        onValueChange={(v) => onChange({ ...filter, channel: v === ALL ? undefined : v })}
        disabled={disabled}
      >
        <SelectTrigger size='sm' aria-label='Channel' className='min-w-40'>
          <SelectValue placeholder='All channels' />
        </SelectTrigger>
        <SelectContent align='start'>
          <SelectItem value={ALL}>All channels</SelectItem>
          {types?.channels.map((c) => (
            <SelectItem key={c.key} value={c.key}>
              {withCount(c.label, c.tickets)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={typeValue(filter.category, filter.intent)}
        onValueChange={(v) => {
          const [category, intent] = v === ALL ? [] : (JSON.parse(v) as string[]);
          onChange({ ...filter, category, intent });
        }}
        disabled={disabled}
      >
        <SelectTrigger size='sm' aria-label='Ticket type' className='min-w-48'>
          <SelectValue placeholder='All ticket types' />
        </SelectTrigger>
        <SelectContent align='start'>
          <SelectItem value={ALL}>All ticket types</SelectItem>
          {types?.categories.map((c) => (
            <SelectGroup key={c.key}>
              <SelectSeparator />
              <SelectLabel>{humanizeKey(c.key)}</SelectLabel>
              <SelectItem value={typeValue(c.key)}>{withCount(`All ${humanizeKey(c.key)}`, c.tickets)}</SelectItem>
              {c.intents.map((i) => (
                <SelectItem key={i.key} value={typeValue(c.key, i.key)} className='pl-5'>
                  {withCount(humanizeKey(i.key), i.tickets)}
                </SelectItem>
              ))}
            </SelectGroup>
          ))}
          {types && types.unclassifiedTickets > 0 ? (
            <>
              <SelectSeparator />
              {/* Information only: unclassified tickets cannot be filtered on. */}
              <SelectItem value={UNCLASSIFIED} disabled>
                {withCount('Unclassified', types.unclassifiedTickets)}
              </SelectItem>
            </>
          ) : null}
        </SelectContent>
      </Select>

      {hasCostFilter(filter) ? (
        <button
          type='button'
          className='rounded text-xs text-[#7C3AED] underline-offset-2 hover:underline focus-visible:underline focus-visible:outline-none'
          onClick={() => onChange({})}
        >
          Clear
        </button>
      ) : null}
    </div>
  );
}
